import React, { useState, useRef, useCallback, useEffect } from 'react';
import { staffAPI } from '../services/api';
import { Camera, CheckCircle, Delete, RotateCcw, LogIn, LogOut } from 'lucide-react';

const KioskCheckIn = () => {
  const [mode, setMode] = useState('pin'); // pin, welcome, camera, confirming, success, error
  const [action, setAction] = useState(null); // check_in, check_out, done — set by server
  const [pin, setPin] = useState('');
  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [staffName, setStaffName] = useState('');
  const [checkTime, setCheckTime] = useState('');
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const resetTimerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState('');
  const [currentDate, setCurrentDate] = useState('');

  // Update clock every second
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setCurrentDate(now.toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }));
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  const resetToHome = useCallback(() => {
    setMode('pin');
    setAction(null);
    setPin('');
    setPhotoBlob(null);
    setPhotoUrl(null);
    setMessage('');
    setError('');
    setStaffName('');
    setCheckTime('');
    setLoading(false);
    stopCamera();
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  // Auto-reset after success/error
  useEffect(() => {
    if (mode === 'success' || mode === 'error') {
      resetTimerRef.current = setTimeout(resetToHome, 5000);
      return () => clearTimeout(resetTimerRef.current);
    }
  }, [mode, resetToHome]);

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera not available. Please use HTTPS or check browser settings.');
        setMode('error');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings and reload the page.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found. Please connect a camera and try again.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is in use by another application. Please close it and try again.');
      } else {
        setError('Unable to access camera. Please check your browser settings and try again.');
      }
      setMode('error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      setPhotoBlob(blob);
      setPhotoUrl(URL.createObjectURL(blob));
      stopCamera();
      setMode('confirming');
    }, 'image/jpeg', 0.8);
  };

  const retakePhoto = () => {
    setPhotoBlob(null);
    setPhotoUrl(null);
    setMode('camera');
    startCamera();
  };

  const handlePinDigit = (digit) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      if (newPin.length === 4) {
        setTimeout(async () => {
          try {
            const result = await staffAPI.validatePin(newPin);
            setStaffName(result.staffName);
            setAction(result.action);

            if (result.action === 'done') {
              setMessage(`You've already checked in and out today.`);
              setMode('success');
              return;
            }

            const actionLabel = result.action === 'check_in' ? 'Checking In' : 'Checking Out';
            setMode('welcome');
            // After welcome, open camera
            setTimeout(() => {
              setMode('camera');
              startCamera();
            }, 1500);
          } catch (err) {
            setError(err.response?.data?.error || 'Invalid PIN. Please try again.');
            setMode('error');
          }
        }, 300);
      }
    }
  };

  const handlePinBackspace = () => {
    setPin(pin.slice(0, -1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const photoFile = photoBlob ? new File([photoBlob], 'photo.jpg', { type: 'image/jpeg' }) : null;
      let result;

      if (action === 'check_in') {
        result = await staffAPI.checkIn(pin, photoFile);
        setCheckTime(new Date(result.checkInTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }));
      } else {
        result = await staffAPI.checkOut(pin, photoFile);
        setCheckTime(new Date(result.checkOutTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }));
      }

      setMessage(result.message);
      setStaffName(result.staffName);
      setMode('success');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setMode('error');
    } finally {
      setLoading(false);
    }
  };

  const isCheckingIn = action === 'check_in';
  const actionColor = isCheckingIn ? '#10b981' : '#ef4444';
  const actionBg = isCheckingIn ? '#065f46' : '#7f1d1d';

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0f172a',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '20px',
      userSelect: 'none',
      touchAction: 'manipulation'
    }}>
      {/* Clock header */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: 0,
        right: 0,
        textAlign: 'center'
      }}>
        <div style={{ color: '#94a3b8', fontSize: '16px', marginBottom: '4px' }}>{currentDate}</div>
        <div style={{ color: 'white', fontSize: '32px', fontWeight: '300' }}>{currentTime}</div>
      </div>

      {/* Logo */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        color: '#10b981',
        fontSize: '24px',
        fontWeight: '700'
      }}>
        KFRESH
      </div>

      {/* Main content area */}
      <div style={{
        width: '100%',
        maxWidth: '500px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px'
      }}>
        {/* PIN ENTRY */}
        {mode === 'pin' && (
          <>
            <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '600', margin: '0 0 4px 0', textAlign: 'center' }}>
              Staff Check In / Out
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '16px', margin: '0 0 8px 0' }}>
              Enter your PIN to get started
            </p>

            {/* PIN dots */}
            <div style={{ display: 'flex', gap: '16px', margin: '10px 0' }}>
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: i < pin.length ? '#10b981' : 'transparent',
                    border: `3px solid ${i < pin.length ? '#10b981' : '#475569'}`,
                    transition: 'all 0.15s'
                  }}
                />
              ))}
            </div>

            {/* Number pad */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              width: '100%',
              maxWidth: '320px'
            }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
                <button
                  key={digit}
                  onClick={() => handlePinDigit(String(digit))}
                  style={{
                    padding: '20px',
                    fontSize: '28px',
                    fontWeight: '500',
                    backgroundColor: '#1e293b',
                    color: 'white',
                    border: '2px solid #334155',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.1s'
                  }}
                >
                  {digit}
                </button>
              ))}
              <div />
              <button
                onClick={() => handlePinDigit('0')}
                style={{
                  padding: '20px',
                  fontSize: '28px',
                  fontWeight: '500',
                  backgroundColor: '#1e293b',
                  color: 'white',
                  border: '2px solid #334155',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                0
              </button>
              <button
                onClick={handlePinBackspace}
                style={{
                  padding: '20px',
                  fontSize: '20px',
                  backgroundColor: '#1e293b',
                  color: '#f59e0b',
                  border: '2px solid #334155',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Delete size={24} />
              </button>
            </div>
          </>
        )}

        {/* WELCOME */}
        {mode === 'welcome' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: actionBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              {isCheckingIn ? <LogIn size={48} color={actionColor} /> : <LogOut size={48} color={actionColor} />}
            </div>
            <h2 style={{ color: actionColor, fontSize: '32px', fontWeight: '600', margin: '0 0 12px 0' }}>
              {isCheckingIn ? `Welcome, ${staffName.split(' ')[0]}!` : `Goodbye, ${staffName.split(' ')[0]}!`}
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '18px', margin: 0 }}>
              {isCheckingIn ? 'Checking you in — get ready for your photo...' : 'Checking you out — get ready for your photo...'}
            </p>
          </div>
        )}

        {/* CAMERA */}
        {mode === 'camera' && (
          <>
            <h2 style={{ color: 'white', fontSize: '24px', fontWeight: '500', margin: 0 }}>
              Take Your Photo
            </h2>
            <div style={{
              width: '100%',
              maxWidth: '400px',
              aspectRatio: '4/3',
              backgroundColor: '#1e293b',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
              />
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <button
              onClick={capturePhoto}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'white',
                border: `4px solid ${actionColor}`,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
            >
              <Camera size={32} color={actionColor} />
            </button>
            <button
              onClick={resetToHome}
              style={{
                padding: '12px 32px',
                backgroundColor: 'transparent',
                color: '#94a3b8',
                border: '2px solid #475569',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </>
        )}

        {/* CONFIRMING */}
        {mode === 'confirming' && (
          <>
            <h2 style={{ color: 'white', fontSize: '24px', fontWeight: '500', margin: 0 }}>
              Confirm Photo
            </h2>
            <div style={{
              width: '100%',
              maxWidth: '400px',
              aspectRatio: '4/3',
              borderRadius: '16px',
              overflow: 'hidden'
            }}>
              {photoUrl && (
                <img
                  src={photoUrl}
                  alt="Captured"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
                />
              )}
            </div>
            <div style={{ display: 'flex', gap: '16px', width: '100%', maxWidth: '400px' }}>
              <button
                onClick={retakePhoto}
                style={{
                  flex: 1,
                  padding: '16px',
                  backgroundColor: '#1e293b',
                  color: '#f59e0b',
                  border: '2px solid #f59e0b',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <RotateCcw size={20} /> Retake
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '16px',
                  backgroundColor: actionBg,
                  color: 'white',
                  border: `2px solid ${actionColor}`,
                  borderRadius: '12px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading ? 'Processing...' : (
                  <>
                    <CheckCircle size={20} />
                    {isCheckingIn ? 'Check In' : 'Check Out'}
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* SUCCESS */}
        {mode === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: '#065f46',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircle size={56} color="#10b981" />
            </div>
            <h2 style={{ color: '#10b981', fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>
              {staffName}
            </h2>
            <p style={{ color: 'white', fontSize: '20px', margin: '0 0 8px 0' }}>
              {message}
            </p>
            {checkTime && (
              <p style={{ color: '#94a3b8', fontSize: '18px', margin: 0 }}>
                Time: {checkTime}
              </p>
            )}
            <p style={{ color: '#475569', fontSize: '14px', marginTop: '24px' }}>
              Returning to home screen in 5 seconds...
            </p>
          </div>
        )}

        {/* ERROR */}
        {mode === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: '#7f1d1d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <span style={{ fontSize: '48px', color: '#ef4444' }}>!</span>
            </div>
            <h2 style={{ color: '#ef4444', fontSize: '24px', fontWeight: '600', margin: '0 0 16px 0' }}>
              {error}
            </h2>
            <button
              onClick={resetToHome}
              style={{
                padding: '16px 40px',
                backgroundColor: '#1e293b',
                color: 'white',
                border: '2px solid #475569',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: '500'
              }}
            >
              Try Again
            </button>
            <p style={{ color: '#475569', fontSize: '14px', marginTop: '16px' }}>
              Returning to home screen in 5 seconds...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KioskCheckIn;
